import {
  addMonths,
  assessAffordability,
  buildForecast,
  calculateMonthlyMetrics,
  evaluateGoal,
  formatMoney,
  generateStabilityInsights,
  generateNotifications,
  moneyFromMajor,
  moneyToMajor,
  type AffordabilityResult,
  type CashFlowEvent,
  type LocalDate,
} from '@expense-assistant/finance-core';
import { validateBackup, type FinancialBackup } from '@expense-assistant/shared';
import {
  ArrowDownLeft,
  ArrowLeftRight,
  ArrowUpRight,
  Bell,
  CalendarDays,
  ChevronRight,
  CircleDollarSign,
  Download,
  Gauge,
  Goal,
  HandCoins,
  Home,
  Landmark,
  Menu,
  Plus,
  Search,
  Settings,
  ShieldCheck,
  Sparkles,
  Upload,
  WalletCards,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { NavLink, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import type { Session } from '@supabase/supabase-js';
import {
  demoData,
  type AppAccount,
  type AppBudgetGroup,
  type AppCategory,
  type AppData,
  type AppDebt,
  type AppGoal,
  type AppPlan,
  type AppTransaction,
} from './data';
import { isDemoMode, supabase } from './supabase';
import { SupabaseFinanceRepository, type FinanceRepository } from './repository';
import { parseMoneyInput } from './money-input';
import {
  consumeRestoreSuccess,
  markRestoreSuccess,
  RESTORE_SUCCESS_MESSAGE,
} from './restore-marker';

const today = financialToday('Asia/Colombo');
// V1 requires at least a 12-month forecast horizon for the dashboard,
// insights, and notification risk generation.
const horizonEnd = addMonths(today, 12);

const nav = [
  { to: '/', label: 'Dashboard', icon: Home },
  { to: '/planner', label: 'Planner', icon: CalendarDays },
  { to: '/transactions', label: 'Transactions', icon: ArrowLeftRight },
  { to: '/accounts', label: 'Accounts', icon: Landmark },
  { to: '/goals', label: 'Goals', icon: Goal },
  { to: '/debts', label: 'Debts', icon: HandCoins },
  { to: '/insights', label: 'Insights', icon: Sparkles },
  { to: '/notifications', label: 'Notifications', icon: Bell },
  { to: '/settings', label: 'Settings', icon: Settings },
];

// Supabase fires onAuthStateChange for benign events (e.g. periodic background
// TOKEN_REFRESHED) with a brand-new session object even though the signed-in
// identity is unchanged. Treating every such event as a "new session" would
// make `session` referentially unstable, which would re-trigger any effect
// keyed on it (see the workspace-loading effect in ProductShell) for no
// reason other than a token rotation. Only the identity fields the app
// actually reads (`user.id`, `user.email`) determine whether the session is
// semantically the same.
function isSameAuthIdentity(a: Session | null, b: Session | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.user.id === b.user.id && a.user.email === b.user.email;
}

function App() {
  const [session, setSession] = useState<Session | null>(null);

  useEffect(() => {
    if (!supabase) return;
    void supabase.auth
      .getSession()
      .then(({ data }) =>
        setSession((current) =>
          isSameAuthIdentity(current, data.session) ? current : data.session,
        ),
      );
    const { data } = supabase.auth.onAuthStateChange((_event, nextSession) =>
      setSession((current) => (isSameAuthIdentity(current, nextSession) ? current : nextSession)),
    );
    return () => data.subscription.unsubscribe();
  }, []);

  if (!isDemoMode && !session) return <AuthScreen />;
  return <ProductShell session={session} />;
}

function AuthScreen() {
  const [mode, setMode] = useState<'LOGIN' | 'REGISTER'>('LOGIN');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!supabase) return;
    setBusy(true);
    setMessage('');
    const result =
      mode === 'LOGIN'
        ? await supabase.auth.signInWithPassword({ email, password })
        : await supabase.auth.signUp({ email, password });
    setBusy(false);
    if (result.error) setMessage(result.error.message);
    else if (mode === 'REGISTER')
      setMessage('Check your email to confirm your account, then sign in.');
  }

  return (
    <main className="auth-layout">
      <section className="auth-story">
        <div className="brand-mark">
          <CircleDollarSign size={26} />
          <span>Cashflow</span>
        </div>
        <div>
          <p className="eyebrow">Know what is safe to spend</p>
          <h1>Plan the timing, not just the total.</h1>
          <p className="auth-copy">
            See upcoming obligations, protect reserves, and test a purchase before it changes your
            plan.
          </p>
        </div>
        <p className="privacy-note">
          <ShieldCheck size={18} /> Your records are isolated by database row-level security.
        </p>
      </section>
      <section className="auth-panel">
        <form onSubmit={(event) => void submit(event)} className="auth-card">
          <p className="eyebrow">
            {mode === 'LOGIN' ? 'Welcome back' : 'Create your private workspace'}
          </p>
          <h2>{mode === 'LOGIN' ? 'Sign in' : 'Register'}</h2>
          <label>
            Email
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </label>
          <label>
            Password
            <input
              type="password"
              minLength={8}
              autoComplete={mode === 'LOGIN' ? 'current-password' : 'new-password'}
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </label>
          {message && (
            <p className="form-message" role="status">
              {message}
            </p>
          )}
          <button className="button primary" disabled={busy}>
            {busy ? 'Please wait…' : mode === 'LOGIN' ? 'Sign in' : 'Create account'}
          </button>
          <button
            type="button"
            className="button text"
            onClick={() => setMode(mode === 'LOGIN' ? 'REGISTER' : 'LOGIN')}
          >
            {mode === 'LOGIN' ? 'New here? Create an account' : 'Already registered? Sign in'}
          </button>
        </form>
      </section>
    </main>
  );
}

function ProductShell({ session }: { session: Session | null }) {
  const [data, setData] = useState<AppData>(demoData);
  const [loading, setLoading] = useState(!isDemoMode);
  const [loadError, setLoadError] = useState('');
  const repository = useMemo(() => (supabase ? new SupabaseFinanceRepository(supabase) : null), []);
  const [mobileNav, setMobileNav] = useState(false);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [affordOpen, setAffordOpen] = useState(false);
  const [toast, setToast] = useState('');
  const location = useLocation();
  const unread = data.notifications.filter((notification) => !notification.read).length;

  useEffect(() => {
    if (consumeRestoreSuccess()) setToast(RESTORE_SUCCESS_MESSAGE);
  }, []);

  useEffect(() => {
    setMobileNav(false);
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  useEffect(() => {
    const onOnline = () => setToast('You are back online.');
    const onOffline = () => setToast('You are offline. New financial changes will not be saved.');
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  useEffect(() => {
    if (!repository || !session) return;
    setLoading(true);
    setLoadError('');
    void repository
      .loadWorkspace(addCalendarDays(today, -365), addCalendarDays(today, 365), today)
      .then(async (workspace) => {
        await repository.refreshNotifications(notificationCandidates(workspace));
        return repository.loadWorkspace(
          addCalendarDays(today, -365),
          addCalendarDays(today, 365),
          today,
        );
      })
      .then(setData)
      .catch(() =>
        setLoadError(
          'Your financial workspace could not be loaded. Check the connection and try again.',
        ),
      )
      .finally(() => setLoading(false));
  }, [repository, session]);

  useEffect(() => {
    const context = document.modelContext;
    if (!context?.registerTool) return;
    const lifecycle = new AbortController();
    const registration = context.registerTool(
      {
        name: 'assess_purchase',
        title: 'Assess a proposed purchase',
        description:
          'Run a read-only cash-flow affordability assessment. This never creates a transaction.',
        inputSchema: {
          type: 'object',
          properties: {
            amountLkr: { type: 'number', exclusiveMinimum: 0 },
            date: { type: 'string', pattern: '^\\d{4}-\\d{2}-\\d{2}$' },
            accountId: { type: 'string' },
            importance: {
              type: 'string',
              enum: ['ESSENTIAL', 'IMPORTANT', 'DISCRETIONARY'],
            },
          },
          required: ['amountLkr', 'date', 'accountId', 'importance'],
          additionalProperties: false,
        },
        annotations: { readOnlyHint: true, untrustedContentHint: false },
        execute(input) {
          if (!isAffordabilityToolInput(input)) throw new Error('Invalid affordability input');
          const result = assessAffordability({
            accounts: data.accounts,
            events: plansToEvents(data.plans),
            from: input.date as LocalDate,
            to: addCalendarDays(input.date as LocalDate, 70),
            safetyBuffer: data.safetyBuffer,
            committedGoalContribution: data.goals
              .filter((goal) => goal.status === 'ACTIVE')
              .reduce((sum, goal) => sum + goal.monthlyContribution, 0),
            proposal: {
              amount: moneyFromMajor(input.amountLkr),
              date: input.date as LocalDate,
              accountId: input.accountId,
              description: 'Proposed purchase',
              importance: input.importance,
            },
          });
          return {
            classification: result.classification,
            minimumProjectedBalanceMinor: result.forecast.minimumAccessibleBalance,
            minimumDate: result.forecast.minimumDate,
            lowerSafeAmountMinor: result.lowerSafeAmount,
            evidence: result.evidence,
          };
        },
      },
      { signal: lifecycle.signal },
    );
    void Promise.resolve(registration).catch(() => {
      // WebMCP is progressive enhancement; the visible workflow remains available.
    });
    return () => lifecycle.abort();
  }, [data]);

  function applyTransaction(transaction: AppTransaction) {
    setData((current) => ({
      ...current,
      accounts: current.accounts.map((account) => {
        if (transaction.type === 'INCOME' && account.id === transaction.accountId)
          return { ...account, balance: account.balance + transaction.amount };
        if (transaction.type === 'EXPENSE' && account.id === transaction.accountId)
          return { ...account, balance: account.balance - transaction.amount };
        if (transaction.type === 'TRANSFER' && account.id === transaction.accountId)
          return { ...account, balance: account.balance - transaction.amount };
        if (transaction.type === 'TRANSFER' && account.id === transaction.toAccountId)
          return { ...account, balance: account.balance + transaction.amount };
        return account;
      }),
      transactions: [transaction, ...current.transactions],
      plans: transaction.plannedEventId
        ? current.plans.map((plan) =>
            plan.id === transaction.plannedEventId
              ? {
                  ...plan,
                  originalAmount: plan.originalAmount ?? plan.amount,
                  amount: Math.max(0, plan.amount - transaction.amount),
                  status: transaction.amount >= plan.amount ? 'SETTLED' : 'PARTIALLY_SETTLED',
                }
              : plan,
          )
        : current.plans,
    }));
    setToast(`${transaction.label} recorded.`);
  }

  function addTransaction(transaction: AppTransaction) {
    if (!repository) {
      applyTransaction(transaction);
      return;
    }
    setToast('Saving money movement…');
    void repository
      .createTransaction(transaction)
      .then((id) => applyTransaction({ ...transaction, id }))
      .catch(() => setToast('The money movement was not saved. Your records were left unchanged.'));
  }

  function addPlannedItem(
    plan: AppPlan,
    category: string,
    notes: string,
    recurring: boolean,
  ): Promise<void> {
    if (!repository) {
      setData((current) => ({ ...current, plans: [...current.plans, plan] }));
      setToast(`${plan.label} ${recurring ? 'recurring plan' : 'plan'} created.`);
      return Promise.resolve();
    }
    setToast('Saving planned item…');
    return repository
      .createPlannedItem(plan, category, notes, recurring)
      .then(() =>
        repository.loadWorkspace(addCalendarDays(today, -365), addCalendarDays(today, 365), today),
      )
      .then((workspace) => {
        setData(workspace);
        setToast(`${plan.label} ${recurring ? 'recurring plan' : 'plan'} created.`);
      })
      .catch((error: unknown) => {
        setToast('The planned item could not be saved.');
        throw error;
      });
  }

  function settlePlan(plan: AppPlan, amount = plan.amount) {
    if (plan.status === 'SETTLED') return;
    if (amount <= 0 || amount > plan.amount) return;
    const settlement = { ...plan, amount };
    if (repository) {
      setToast(`Saving ${plan.type === 'INCOME' ? 'receipt' : 'payment'}…`);
      void repository
        .settlePlan(settlement, today, crypto.randomUUID())
        .then((transactionId) => applyPlanSettlement(plan, amount, transactionId))
        .catch(() => setToast('The settlement was not saved. Your records were left unchanged.'));
      return;
    }
    applyPlanSettlement(plan, amount);
  }

  function applyPlanSettlement(
    plan: AppPlan,
    amount: number,
    transactionId: string = crypto.randomUUID(),
  ) {
    const transaction: AppTransaction = {
      id: transactionId,
      type: plan.type,
      label: plan.label,
      amount,
      date: today,
      accountId: plan.accountId,
      ...(plan.type === 'TRANSFER' ? { toAccountId: plan.toAccountId } : {}),
      category: plan.type === 'INCOME' ? 'Salary' : 'Other',
      group: plan.group,
      status: 'COMPLETED',
    };
    setData((current) => ({
      ...current,
      accounts: current.accounts.map((account) => {
        if (account.id === plan.accountId)
          return {
            ...account,
            balance: account.balance + (plan.type === 'INCOME' ? amount : -amount),
          };
        if (plan.type === 'TRANSFER' && account.id === plan.toAccountId)
          return { ...account, balance: account.balance + amount };
        return account;
      }),
      transactions: [transaction, ...current.transactions],
      plans: current.plans.map((item) =>
        item.id === plan.id
          ? {
              ...item,
              originalAmount: item.originalAmount ?? item.amount,
              amount: Math.max(0, item.amount - amount),
              status: amount >= item.amount ? 'SETTLED' : 'PARTIALLY_SETTLED',
            }
          : item,
      ),
    }));
    setToast(
      `${plan.label} ${amount >= plan.amount ? 'fully' : 'partially'} ${plan.type === 'INCOME' ? 'received' : plan.type === 'TRANSFER' ? 'moved' : 'paid'}.`,
    );
  }

  function createAccount(account: AppAccount) {
    if (!repository) {
      setData((current) => ({ ...current, accounts: [...current.accounts, account] }));
      return Promise.resolve();
    }
    return repository
      .createAccount(account, today)
      .then(() => {
        setData((current) => ({ ...current, accounts: [...current.accounts, account] }));
        setToast(`${account.name} created.`);
      })
      .catch((error: unknown) => {
        setToast('The account could not be created.');
        throw error;
      });
  }

  function createGoal(goal: AppGoal) {
    if (!repository) {
      setData((current) => ({ ...current, goals: [...current.goals, goal] }));
      return Promise.resolve();
    }
    return repository
      .createGoal(goal, today)
      .then(() => {
        setData((current) => ({ ...current, goals: [...current.goals, goal] }));
        setToast(`${goal.name} created.`);
      })
      .catch((error: unknown) => {
        setToast('The goal could not be created.');
        throw error;
      });
  }

  function allocateToGoal(goal: AppGoal, kind: 'ALLOCATE' | 'RELEASE', amount: number) {
    const apply = () =>
      setData((current) => ({
        ...current,
        goals: current.goals.map((item) =>
          item.id === goal.id
            ? {
                ...item,
                allocated:
                  kind === 'ALLOCATE'
                    ? item.allocated + amount
                    : Math.max(0, item.allocated - amount),
              }
            : item,
        ),
      }));
    if (!repository) {
      apply();
      return;
    }
    setToast(kind === 'ALLOCATE' ? 'Saving allocation…' : 'Saving release…');
    void repository
      .recordGoalAllocation(goal.id, kind, amount, today, crypto.randomUUID())
      .then(() => {
        apply();
        setToast(kind === 'ALLOCATE' ? 'Allocation added.' : 'Allocation released.');
      })
      .catch(() => setToast('The goal allocation could not be saved.'));
  }

  function createDebt(debt: AppDebt) {
    if (!repository) {
      setData((current) => ({ ...current, debts: [...current.debts, debt] }));
      return Promise.resolve();
    }
    return repository
      .createDebt(debt, today)
      .then(() => {
        setData((current) => ({ ...current, debts: [...current.debts, debt] }));
        setToast(`${debt.counterparty} record created.`);
      })
      .catch((error: unknown) => {
        setToast('The debt/receivable could not be created.');
        throw error;
      });
  }

  function settleDebt(
    debt: AppDebt,
    amount: number,
    accountId: string,
    date: string = today,
    notes?: string,
  ) {
    const fundingAccount = data.accounts.find((item) => item.id === accountId);
    if (!fundingAccount) {
      setToast('Choose a valid account for this settlement.');
      return;
    }
    const apply = (transactionId: string = crypto.randomUUID()) =>
      setData((current) => ({
        ...current,
        debts: current.debts.map((item) => {
          if (item.id !== debt.id) return item;
          const remainingAmount = Math.max(0, item.remainingAmount - amount);
          return {
            ...item,
            remainingAmount,
            status: remainingAmount === 0 ? 'SETTLED' : 'PARTIAL',
          };
        }),
        accounts: current.accounts.map((account) =>
          account.id === fundingAccount.id
            ? {
                ...account,
                balance: account.balance + (debt.direction === 'OWED_TO_ME' ? amount : -amount),
              }
            : account,
        ),
        transactions: [
          {
            id: transactionId,
            type: debt.direction === 'OWED_TO_ME' ? ('INCOME' as const) : ('EXPENSE' as const),
            label: `Debt settlement: ${debt.counterparty}`,
            amount,
            date: date as LocalDate,
            accountId: fundingAccount.id,
            category: 'Other',
            group: 'Ungrouped',
            ...(notes ? { notes } : {}),
            status: 'COMPLETED' as const,
          },
          ...current.transactions,
        ],
      }));
    if (!repository) {
      apply();
      return;
    }
    setToast('Saving debt settlement…');
    void repository
      .settleDebt(debt.id, amount, fundingAccount.id, date, crypto.randomUUID(), notes)
      .then((transactionId) => {
        apply(transactionId);
        setToast('Debt settlement recorded.');
      })
      .catch(() => setToast('The debt settlement was not saved.'));
  }

  function markNotification(notificationId: string) {
    const apply = () =>
      setData((current) => ({
        ...current,
        notifications: current.notifications.map((item) =>
          item.id === notificationId ? { ...item, read: true } : item,
        ),
      }));
    if (!repository) {
      apply();
      return;
    }
    void repository
      .markNotificationRead(notificationId)
      .then(apply)
      .catch(() => setToast('The notification could not be updated.'));
  }

  function markAllNotifications() {
    const apply = () =>
      setData((current) => ({
        ...current,
        notifications: current.notifications.map((item) => ({ ...item, read: true })),
      }));
    if (!repository) {
      apply();
      return;
    }
    void repository
      .markAllNotificationsRead()
      .then(apply)
      .catch(() => setToast('Notifications could not be updated.'));
  }

  if (loading)
    return (
      <div className="loading-screen">
        <div className="brand-mark">
          <CircleDollarSign />
          <span>Cashflow</span>
        </div>
        <p>Loading your private workspace…</p>
      </div>
    );
  if (loadError)
    return (
      <div className="loading-screen error-state">
        <ShieldCheck />
        <h1>Workspace unavailable</h1>
        <p>{loadError}</p>
        <button className="button secondary" onClick={() => window.location.reload()}>
          Try again
        </button>
      </div>
    );

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <aside className={`sidebar ${mobileNav ? 'open' : ''}`}>
        <div className="brand-mark">
          <CircleDollarSign size={25} />
          <span>Cashflow</span>
        </div>
        <nav aria-label="Primary">
          {nav.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
            >
              <Icon size={19} />
              <span>{label}</span>
              {label === 'Notifications' && unread > 0 && (
                <span className="nav-badge">{unread}</span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="sidebar-footer">
          <span className={`connection-dot ${navigator.onLine ? '' : 'offline'}`} />
          <div>
            <strong>{isDemoMode ? 'Demo workspace' : session?.user.email}</strong>
            <small>{navigator.onLine ? 'Online' : 'Offline · read only'}</small>
          </div>
        </div>
      </aside>
      {mobileNav && (
        <button
          className="nav-scrim"
          aria-label="Close navigation"
          onClick={() => setMobileNav(false)}
        />
      )}
      <div className="main-column">
        <header className="topbar">
          <button
            className="icon-button mobile-only"
            aria-label="Open navigation"
            onClick={() => setMobileNav(true)}
          >
            <Menu />
          </button>
          <div className="topbar-spacer" />
          {isDemoMode && <span className="demo-pill">Demo data</span>}
          <button
            className="icon-button notification-button"
            aria-label={`${unread} unread notifications`}
            onClick={() => {
              window.location.href = '/notifications';
            }}
          >
            <Bell />
            {unread > 0 && <span>{unread}</span>}
          </button>
          <button className="button secondary quick-top" onClick={() => setQuickAddOpen(true)}>
            <Plus size={18} />
            Quick add
          </button>
        </header>
        <main id="main" className="page-shell">
          <Routes>
            <Route
              path="/"
              element={
                <Dashboard
                  data={data}
                  onAfford={() => setAffordOpen(true)}
                  onQuickAdd={() => setQuickAddOpen(true)}
                  onSettle={settlePlan}
                />
              }
            />
            <Route path="/planner" element={<Planner data={data} onSettle={settlePlan} />} />
            <Route path="/transactions" element={<Transactions data={data} />} />
            <Route path="/accounts" element={<Accounts data={data} onCreate={createAccount} />} />
            <Route
              path="/goals"
              element={<Goals data={data} onCreate={createGoal} onAllocate={allocateToGoal} />}
            />
            <Route
              path="/debts"
              element={<Debts data={data} onCreate={createDebt} onSettle={settleDebt} />}
            />
            <Route path="/insights" element={<Insights data={data} />} />
            <Route
              path="/notifications"
              element={
                <Notifications
                  data={data}
                  onMark={markNotification}
                  onMarkAll={markAllNotifications}
                />
              }
            />
            <Route
              path="/settings"
              element={
                <SettingsPage
                  data={data}
                  setData={setData}
                  session={session}
                  setToast={setToast}
                  repository={repository}
                />
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
      <button className="floating-add" onClick={() => setQuickAddOpen(true)} aria-label="Quick add">
        <Plus />
      </button>
      {quickAddOpen && (
        <QuickAdd
          data={data}
          onClose={() => setQuickAddOpen(false)}
          onAdd={(transaction) => {
            addTransaction(transaction);
            setQuickAddOpen(false);
          }}
          onPlan={(plan, category, notes, recurring) =>
            addPlannedItem(plan, category, notes, recurring).then(() => setQuickAddOpen(false))
          }
        />
      )}
      {affordOpen && (
        <Affordability
          data={data}
          onClose={() => setAffordOpen(false)}
          onProceed={(transaction) => {
            addTransaction(transaction);
            setAffordOpen(false);
          }}
          onSchedule={(plan, category, notes) =>
            addPlannedItem(plan, category, notes, false).then(() => setAffordOpen(false))
          }
        />
      )}
      {toast && (
        <div className="toast" role="status">
          <span>{toast}</span>
          <button aria-label="Dismiss" onClick={() => setToast('')}>
            <X size={16} />
          </button>
        </div>
      )}
    </div>
  );
}

function Dashboard({
  data,
  onAfford,
  onQuickAdd,
  onSettle,
}: {
  data: AppData;
  onAfford(): void;
  onQuickAdd(): void;
  onSettle(plan: AppPlan, amount?: number): void;
}) {
  const currentMonthPlans = data.plans.filter((plan) => plan.date.startsWith(today.slice(0, 7)));
  const metrics = referenceMetrics(currentMonthPlans);
  if (data.accounts.length === 0) {
    return (
      <>
        <PageHeading
          eyebrow="Private workspace ready"
          title="Start with the money you have"
          description="Create your first account. Then add income and obligations so forecasts use real balances instead of assumptions."
        />
        <section className="panel onboarding-card">
          <span className="goal-icon">
            <Landmark />
          </span>
          <h2>Create your first account</h2>
          <p>
            Add a salary account, savings, emergency reserve, cash, or another owned account.
            Protected accounts will stay outside normal spending calculations.
          </p>
          <NavLink className="button primary" to="/accounts">
            Create an account <ChevronRight size={17} />
          </NavLink>
        </section>
      </>
    );
  }
  const liquidAboveMinimums = data.accounts
    .filter((account) => account.spendable && !account.protected)
    .reduce((sum, account) => sum + Math.max(0, account.balance - account.minimumBalance), 0);
  const spendable = Math.max(0, liquidAboveMinimums - data.safetyBuffer);
  const protectedCash = data.accounts
    .filter((account) => account.protected)
    .reduce((sum, account) => sum + account.balance, 0);
  const remainingIncome = currentMonthPlans
    .filter((plan) => plan.type === 'INCOME' && plan.status !== 'SETTLED')
    .reduce((sum, plan) => sum + plan.amount, 0);
  const remainingExpense = currentMonthPlans
    .filter((plan) => plan.type === 'EXPENSE' && plan.status !== 'SETTLED')
    .reduce((sum, plan) => sum + plan.amount, 0);
  const events = plansToEvents(data.plans);
  const forecast = buildForecast({
    accounts: data.accounts,
    events,
    from: today,
    to: horizonEnd,
    safetyBuffer: data.safetyBuffer,
  });
  const nextPlans = data.plans
    .filter((plan) => plan.status !== 'SETTLED')
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(0, 5);

  return (
    <>
      <PageHeading
        eyebrow={formatFinancialDate(today)}
        title="Here’s your cash position"
        action={
          <button className="button secondary" onClick={onQuickAdd}>
            <Plus size={18} />
            Add money movement
          </button>
        }
      />
      <section className="hero-grid">
        <article className="balance-card">
          <div>
            <p className="card-label">Available to spend</p>
            <h2>{formatMoney(spendable)}</h2>
            <p>
              Across{' '}
              {data.accounts.filter((account) => account.spendable && !account.protected).length}{' '}
              spendable accounts
            </p>
          </div>
          <div className="balance-rule" />
          <div className="balance-details">
            <span>
              Protected reserve <strong>{formatMoney(protectedCash)}</strong>
            </span>
            <span>
              Safety buffer <strong>{formatMoney(data.safetyBuffer)}</strong>
            </span>
          </div>
        </article>
        <button className="afford-card" onClick={onAfford}>
          <span className="afford-icon">
            <Gauge size={28} />
          </span>
          <span>
            <small>CHECK BEFORE SPENDING</small>
            <strong>Can I afford this?</strong>
            <em>Test a purchase against the next 60+ days.</em>
          </span>
          <ChevronRight />
        </button>
      </section>
      <section className="metric-grid" aria-label="Monthly summary">
        <Metric
          label="Income remaining"
          value={formatMoney(remainingIncome)}
          icon={<ArrowDownLeft />}
          tone="positive"
        />
        <Metric
          label="Expenses remaining"
          value={formatMoney(remainingExpense)}
          icon={<ArrowUpRight />}
          tone="negative"
        />
        <Metric
          label="Planned monthly surplus"
          value={formatMoney(metrics.surplus)}
          icon={<WalletCards />}
          tone="neutral"
          detail={`${((metrics.surplusRateBasisPoints ?? 0) / 100).toFixed(1)}% surplus rate`}
        />
        <Metric
          label="Forecast low point"
          value={formatMoney(forecast.minimumAccessibleBalance)}
          icon={<Gauge />}
          tone={forecast.safetyBufferBreach ? 'warning' : 'positive'}
          detail={forecast.minimumDate}
        />
      </section>
      <section className="content-grid">
        <article className="panel upcoming-panel">
          <PanelTitle title="Next up" meta="Your upcoming money" link="/planner" />
          <div className="timeline-list">
            {nextPlans.map((plan) => (
              <PlanRow key={plan.id} plan={plan} onSettle={onSettle} />
            ))}
          </div>
        </article>
        <article className="panel warning-panel">
          <PanelTitle title="Cash-flow watch" meta="Timing matters" />
          <div className="warning-body">
            <div className="warning-gauge">
              <span
                style={{
                  width: `${Math.min(100, Math.max(8, (forecast.minimumAccessibleBalance / Math.max(spendable, 1)) * 100))}%`,
                }}
              />
            </div>
            <h3>
              {forecast.safetyBufferBreach
                ? 'Your buffer may be tight'
                : 'Your plan stays above the buffer'}
            </h3>
            <p>
              The lowest projected available cash is{' '}
              <strong>{formatMoney(forecast.minimumAccessibleBalance)}</strong> on{' '}
              {forecast.minimumDate}. This includes scheduled obligations and expected income.
            </p>
            <button className="button text-inline" onClick={onAfford}>
              Test an expense <ChevronRight size={16} />
            </button>
          </div>
        </article>
      </section>
      <section className="panel accounts-preview">
        <PanelTitle title="Your accounts" meta="Current position" link="/accounts" />
        <div className="account-row-grid">
          {data.accounts.map((account) => (
            <div className="account-mini" key={account.id}>
              <div className={`account-icon ${account.protected ? 'protected' : ''}`}>
                {account.protected ? <ShieldCheck /> : <Landmark />}
              </div>
              <span>
                <strong>{account.name}</strong>
                <small>
                  {account.type}
                  {account.protected ? ' · Protected' : ''}
                </small>
              </span>
              <b>{formatMoney(account.balance)}</b>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}

function Planner({
  data,
  onSettle,
}: {
  data: AppData;
  onSettle(plan: AppPlan, amount?: number): void;
}) {
  const [month, setMonth] = useState(today.slice(0, 7));
  const monthLabel = new Date(`${month}-01T00:00:00`).toLocaleDateString('en', {
    month: 'long',
    year: 'numeric',
  });
  const monthPlans = data.plans.filter((plan) => plan.date.startsWith(month));
  const monthTransactions = data.transactions.filter((transaction) =>
    transaction.date.startsWith(month),
  );
  const groups = [...monthPlans]
    .sort((a, b) => a.date.localeCompare(b.date))
    .reduce<Record<string, AppPlan[]>>((result, plan) => {
      (result[plan.date] ??= []).push(plan);
      return result;
    }, {});
  const transactionGroups = [...monthTransactions]
    .sort((a, b) => a.date.localeCompare(b.date))
    .reduce<Record<string, AppTransaction[]>>((result, transaction) => {
      (result[transaction.date] ??= []).push(transaction);
      return result;
    }, {});
  const dates = [...new Set([...Object.keys(groups), ...Object.keys(transactionGroups)])].sort();
  const metrics = referenceMetrics(monthPlans);
  const shiftMonth = (amount: number) => {
    const cursor = new Date(`${month}-01T00:00:00`);
    cursor.setMonth(cursor.getMonth() + amount);
    setMonth(
      `${String(cursor.getFullYear()).padStart(4, '0')}-${String(cursor.getMonth() + 1).padStart(2, '0')}`,
    );
  };
  return (
    <>
      <PageHeading
        eyebrow={monthLabel}
        title="Monthly planner"
        description="Expected and actual money, ordered by the day it affects your cash."
        action={
          <div className="month-switch">
            <button aria-label="Previous month" onClick={() => shiftMonth(-1)}>
              ‹
            </button>
            <strong>{monthLabel}</strong>
            <button aria-label="Next month" onClick={() => shiftMonth(1)}>
              ›
            </button>
          </div>
        }
      />
      <div className="planner-layout">
        <section className="panel planner-list">
          {dates.map((date) => (
            <div className="planner-day" key={date}>
              <div className="day-badge">
                <strong>
                  {new Date(`${date}T00:00:00`).toLocaleDateString('en', { day: '2-digit' })}
                </strong>
                <span>
                  {new Date(`${date}T00:00:00`).toLocaleDateString('en', { weekday: 'short' })}
                </span>
              </div>
              <div className="day-events">
                {groups[date]?.map((plan) => (
                  <PlanRow key={plan.id} plan={plan} onSettle={onSettle} />
                ))}
                {transactionGroups[date]?.map((transaction) => (
                  <ActualRow key={transaction.id} transaction={transaction} />
                ))}
              </div>
            </div>
          ))}
          {dates.length === 0 && (
            <Empty
              title="No activity this month"
              body="Navigate to another month or add a money movement."
            />
          )}
        </section>
        <aside className="panel month-summary">
          <p className="card-label">MONTH AT A GLANCE</p>
          <SummaryLine
            label="Expected income"
            value={formatMoney(metrics.income)}
            tone="positive"
          />
          <SummaryLine
            label="Planned expenses"
            value={formatMoney(metrics.expenses)}
            tone="negative"
          />
          <SummaryLine
            label="Projected surplus"
            value={formatMoney(metrics.surplus)}
            tone={metrics.surplus >= 0 ? 'positive' : 'negative'}
            large
          />
          <div className="summary-note">
            <Gauge size={20} />
            <p>
              <strong>Date-by-date check</strong>
              <br />A monthly surplus can still contain a temporary shortfall. Use the timeline and
              affordability check before committing cash.
            </p>
          </div>
        </aside>
      </div>
    </>
  );
}

function Transactions({ data }: { data: AppData }) {
  const [query, setQuery] = useState('');
  const [type, setType] = useState('ALL');
  const [account, setAccount] = useState('ALL');
  const [category, setCategory] = useState('ALL');
  const [group, setGroup] = useState('ALL');
  const filtered = data.transactions.filter(
    (transaction) =>
      (type === 'ALL' || transaction.type === type) &&
      (account === 'ALL' || transaction.accountId === account) &&
      (category === 'ALL' || transaction.category === category) &&
      (group === 'ALL' || transaction.group === group) &&
      `${transaction.label} ${transaction.category} ${transaction.group}`
        .toLowerCase()
        .includes(query.toLowerCase()),
  );
  return (
    <>
      <PageHeading
        eyebrow="History"
        title="Transactions"
        description="Actual money movements only. Transfers are excluded from income and expense totals."
      />
      <section className="panel">
        <div className="filter-bar">
          <label className="search-field">
            <Search size={18} />
            <input
              aria-label="Search transactions"
              placeholder="Search transactions"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>
          <select
            aria-label="Transaction type"
            value={type}
            onChange={(event) => setType(event.target.value)}
          >
            <option value="ALL">All types</option>
            <option value="INCOME">Income</option>
            <option value="EXPENSE">Expense</option>
            <option value="TRANSFER">Transfer</option>
          </select>
          <select
            aria-label="Account"
            value={account}
            onChange={(event) => setAccount(event.target.value)}
          >
            <option value="ALL">All accounts</option>
            {data.accounts.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
          <select
            aria-label="Category"
            value={category}
            onChange={(event) => setCategory(event.target.value)}
          >
            <option value="ALL">All categories</option>
            {[...new Set(data.transactions.map((item) => item.category))].map((item) => (
              <option key={item}>{item}</option>
            ))}
          </select>
          <select
            aria-label="Budget group"
            value={group}
            onChange={(event) => setGroup(event.target.value)}
          >
            <option value="ALL">All groups</option>
            {[...new Set(data.transactions.map((item) => item.group))].map((item) => (
              <option key={item}>{item}</option>
            ))}
          </select>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Description</th>
                <th>Category</th>
                <th>Group</th>
                <th>Type</th>
                <th className="align-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((transaction) => (
                <tr key={transaction.id}>
                  <td>{transaction.date}</td>
                  <td>
                    <strong>{transaction.label}</strong>
                  </td>
                  <td>{transaction.category}</td>
                  <td>{transaction.group}</td>
                  <td>
                    <Status
                      text={transaction.type}
                      tone={
                        transaction.type === 'INCOME'
                          ? 'positive'
                          : transaction.type === 'EXPENSE'
                            ? 'negative'
                            : 'neutral'
                      }
                    />
                  </td>
                  <td
                    className={`align-right amount ${transaction.type === 'INCOME' ? 'positive-text' : transaction.type === 'EXPENSE' ? 'negative-text' : ''}`}
                  >
                    {transaction.type === 'INCOME'
                      ? '+'
                      : transaction.type === 'EXPENSE'
                        ? '−'
                        : ''}
                    {formatMoney(transaction.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <Empty
            title="No transactions match"
            body="Change the filters or record a new money movement."
          />
        )}
      </section>
    </>
  );
}

function Accounts({
  data,
  onCreate,
}: {
  data: AppData;
  onCreate(account: AppAccount): Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <PageHeading
        eyebrow="Balances"
        title="Accounts"
        description="Opening balances plus actual movements determine each balance."
        action={
          <button className="button secondary" onClick={() => setOpen(true)}>
            <Plus size={18} />
            New account
          </button>
        }
      />
      <section className="account-cards">
        {data.accounts.map((account) => (
          <article className="panel account-card" key={account.id}>
            <div className="account-card-top">
              <div className={`account-icon ${account.protected ? 'protected' : ''}`}>
                {account.protected ? <ShieldCheck /> : <Landmark />}
              </div>
              <Status
                text={
                  account.protected ? 'PROTECTED' : account.spendable ? 'SPENDABLE' : 'RESERVED'
                }
                tone={account.protected ? 'warning' : 'positive'}
              />
            </div>
            <p>{account.name}</p>
            <h2>{formatMoney(account.balance)}</h2>
            <div className="account-meta">
              <span>
                Type <strong>{account.type}</strong>
              </span>
              <span>
                Minimum <strong>{formatMoney(account.minimumBalance)}</strong>
              </span>
            </div>
          </article>
        ))}
      </section>
      {open && (
        <NewAccount
          onClose={() => setOpen(false)}
          onCreate={(account) => {
            void onCreate(account)
              .then(() => setOpen(false))
              .catch(() => undefined);
          }}
        />
      )}
    </>
  );
}

function Goals({
  data,
  onCreate,
  onAllocate,
}: {
  data: AppData;
  onCreate(goal: AppGoal): Promise<void>;
  onAllocate(goal: AppGoal, kind: 'ALLOCATE' | 'RELEASE', amount: number): void;
}) {
  const [open, setOpen] = useState(false);
  const [allocatingGoal, setAllocatingGoal] = useState<AppGoal | null>(null);
  return (
    <>
      <PageHeading
        eyebrow="Future plans"
        title="Goals"
        description="Feasibility uses your current recurring plan and assumes zero investment return."
        action={
          <button className="button secondary" onClick={() => setOpen(true)}>
            <Plus size={18} />
            New goal
          </button>
        }
      />
      <section className="goal-grid">
        {data.goals.map((goal) => {
          const monthlyFreeCash = referenceMetrics(
            data.plans.filter((plan) => plan.date.startsWith(today.slice(0, 7))),
          ).surplus;
          const result = evaluateGoal({
            targetAmount: goal.target,
            currentAllocated: goal.allocated,
            targetDate: goal.targetDate,
            asOfDate: today,
            plannedMonthlyContribution: goal.monthlyContribution,
            forecastMonthlyFreeCash: monthlyFreeCash,
          });
          const percentage = Math.round((goal.allocated / goal.target) * 100);
          return (
            <article className="panel goal-card" key={goal.id}>
              <div className="goal-heading">
                <span className="goal-icon">
                  <Goal />
                </span>
                <div>
                  <Status
                    text={`${goal.priority} PRIORITY`}
                    tone={goal.priority === 'HIGH' ? 'warning' : 'neutral'}
                  />
                  <h2>{goal.name}</h2>
                </div>
              </div>
              <div className="goal-progress">
                <div>
                  <span>Allocated</span>
                  <strong>
                    {formatMoney(goal.allocated)} <small>of {formatMoney(goal.target)}</small>
                  </strong>
                </div>
                <b>{percentage}%</b>
              </div>
              <div className="progress-track">
                <span style={{ width: `${percentage}%` }} />
              </div>
              <div className="goal-facts">
                <span>
                  Required monthly<strong>{formatMoney(result.requiredMonthlyContribution)}</strong>
                </span>
                <span>
                  Current plan<strong>{formatMoney(goal.monthlyContribution)}</strong>
                </span>
                <span>
                  Contribution gap
                  <strong className={result.contributionGap > 0 ? 'warning-text' : 'positive-text'}>
                    {formatMoney(result.contributionGap)}
                  </strong>
                </span>
              </div>
              <p className="goal-verdict">
                {result.feasibleAtPlannedContribution
                  ? 'On track based on the current recurring plan.'
                  : result.cashFlowFeasibilityReason === 'FREE_CASH_DEFICIT'
                    ? `Your current monthly plan has a projected cash-flow deficit of ${formatMoney(-result.forecastMonthlyFreeCash)}. Reduce planned spending or increase income before committing more to this goal.`
                    : result.cashFlowFeasibilityReason === 'OTHER_CONSTRAINT'
                      ? 'Your monthly cash flow may not reliably support this contribution due to account minimums, safety buffers, or reserved balances. Review the forecast before increasing your commitment.'
                      : `Increase the monthly contribution by ${formatMoney(result.contributionGap)} or adjust the target date.`}
              </p>
              <div className="row-buttons">
                <button className="button small secondary" onClick={() => setAllocatingGoal(goal)}>
                  Add / release funds
                </button>
              </div>
            </article>
          );
        })}
      </section>
      {open && (
        <NewGoal
          onClose={() => setOpen(false)}
          onCreate={(goal) => {
            void onCreate(goal)
              .then(() => setOpen(false))
              .catch(() => undefined);
          }}
        />
      )}
      {allocatingGoal && (
        <GoalAllocation
          goal={allocatingGoal}
          onClose={() => setAllocatingGoal(null)}
          onAllocate={(kind, amount) => {
            onAllocate(allocatingGoal, kind, amount);
            setAllocatingGoal(null);
          }}
        />
      )}
    </>
  );
}

function GoalAllocation({
  goal,
  onClose,
  onAllocate,
}: {
  goal: AppGoal;
  onClose(): void;
  onAllocate(kind: 'ALLOCATE' | 'RELEASE', amount: number): void;
}) {
  const [kind, setKind] = useState<'ALLOCATE' | 'RELEASE'>('ALLOCATE');
  const [amountText, setAmountText] = useState('');
  const [error, setError] = useState('');
  function submit(event: FormEvent) {
    event.preventDefault();
    const amount = parseMoneyInput(amountText);
    if (amount === null || amount <= 0) {
      setError('Enter a valid amount greater than zero, e.g. 5000 or 5000.50.');
      return;
    }
    if (kind === 'RELEASE' && amount > goal.allocated) {
      setError('Cannot release more than the currently allocated amount.');
      return;
    }
    setError('');
    onAllocate(kind, amount);
  }
  return (
    <Modal
      title={`Adjust allocation · ${goal.name}`}
      subtitle={`Currently allocated: ${formatMoney(goal.allocated)} of ${formatMoney(goal.target)}.`}
      onClose={onClose}
    >
      <form onSubmit={submit}>
        <div className="form-grid">
          <label>
            Action
            <select value={kind} onChange={(event) => setKind(event.target.value as typeof kind)}>
              <option value="ALLOCATE">Add funds to this goal</option>
              <option value="RELEASE">Release funds from this goal</option>
            </select>
          </label>
          <label>
            Amount (LKR)
            <input
              autoFocus
              required
              inputMode="decimal"
              value={amountText}
              onChange={(event) => {
                setAmountText(event.target.value);
                setError('');
              }}
            />
          </label>
        </div>
        {error && (
          <small className="field-error" role="alert">
            {error}
          </small>
        )}
        <div className="modal-actions">
          <button type="button" className="button secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="button primary">
            {kind === 'ALLOCATE' ? 'Add funds' : 'Release funds'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function Debts({
  data,
  onCreate,
  onSettle,
}: {
  data: AppData;
  onCreate(debt: AppDebt): Promise<void>;
  onSettle(debt: AppDebt, amount: number, accountId: string, date: string, notes?: string): void;
}) {
  const [open, setOpen] = useState(false);
  const [settlingDebt, setSettlingDebt] = useState<AppDebt | null>(null);
  return (
    <>
      <PageHeading
        eyebrow="People & obligations"
        title="Debts and receivables"
        description="Track money you owe and money owed to you, including partial settlements."
        action={
          <button className="button secondary" onClick={() => setOpen(true)}>
            <Plus size={18} />
            New record
          </button>
        }
      />
      <section className="panel debt-list">
        {data.debts.map((debt) => (
          <div className="debt-row" key={debt.id}>
            <div className={`debt-direction ${debt.direction === 'OWED_TO_ME' ? 'in' : 'out'}`}>
              {debt.direction === 'OWED_TO_ME' ? <ArrowDownLeft /> : <ArrowUpRight />}
            </div>
            <div className="debt-name">
              <strong>{debt.counterparty}</strong>
              <span>
                {debt.direction === 'OWED_TO_ME' ? 'Owes you' : 'You owe'} · Due {debt.dueDate}
              </span>
            </div>
            <div className="debt-amount">
              <strong>{formatMoney(debt.remainingAmount)}</strong>
              <span>of {formatMoney(debt.originalAmount)} remaining</span>
            </div>
            <Status
              text={debt.status}
              tone={
                debt.status === 'SETTLED'
                  ? 'positive'
                  : debt.status === 'OVERDUE'
                    ? 'negative'
                    : 'warning'
              }
            />
            {debt.status !== 'SETTLED' && (
              <div className="row-buttons">
                <button className="button small secondary" onClick={() => setSettlingDebt(debt)}>
                  Settle
                </button>
              </div>
            )}
          </div>
        ))}
      </section>
      {open && (
        <NewDebt
          onClose={() => setOpen(false)}
          onCreate={(debt) => {
            void onCreate(debt)
              .then(() => setOpen(false))
              .catch(() => undefined);
          }}
        />
      )}
      {settlingDebt && (
        <SettleDebt
          debt={settlingDebt}
          accounts={data.accounts}
          onClose={() => setSettlingDebt(null)}
          onSettle={(amount, accountId, date, notes) => {
            onSettle(settlingDebt, amount, accountId, date, notes);
            setSettlingDebt(null);
          }}
        />
      )}
    </>
  );
}

function SettleDebt({
  debt,
  accounts,
  onClose,
  onSettle,
}: {
  debt: AppDebt;
  accounts: AppAccount[];
  onClose(): void;
  onSettle(amount: number, accountId: string, date: string, notes?: string): void;
}) {
  const eligibleAccounts = accounts.filter((account) => account.spendable);
  const defaultAccount =
    eligibleAccounts.find((account) => !account.protected) ?? eligibleAccounts[0];
  const [amountText, setAmountText] = useState(moneyToMajor(debt.remainingAmount));
  const [accountId, setAccountId] = useState(defaultAccount?.id ?? '');
  const [date, setDate] = useState(today);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const isPaying = debt.direction === 'I_OWE';
  function submit(event: FormEvent) {
    event.preventDefault();
    const amount = parseMoneyInput(amountText);
    if (amount === null || amount <= 0) {
      setError('Enter a valid amount greater than zero, e.g. 5000 or 5000.50.');
      return;
    }
    if (amount > debt.remainingAmount) {
      setError(
        `Amount cannot exceed the remaining balance of ${formatMoney(debt.remainingAmount)}.`,
      );
      return;
    }
    if (!accountId) {
      setError('Choose an account.');
      return;
    }
    setError('');
    onSettle(amount, accountId, date, notes.trim() || undefined);
  }
  return (
    <Modal
      title={`Settle · ${debt.counterparty}`}
      subtitle={
        isPaying
          ? 'This records money you are paying out to settle what you owe.'
          : 'This records money you are receiving to settle what is owed to you.'
      }
      onClose={onClose}
    >
      <form onSubmit={submit}>
        <p className="form-message">
          {isPaying ? 'You are paying' : 'You are receiving'} up to{' '}
          {formatMoney(debt.remainingAmount)} remaining.
        </p>
        <div className="form-grid">
          <label>
            Amount (LKR)
            <input
              autoFocus
              required
              inputMode="decimal"
              value={amountText}
              onChange={(event) => {
                setAmountText(event.target.value);
                setError('');
              }}
            />
          </label>
          <label>
            Account
            <select value={accountId} onChange={(event) => setAccountId(event.target.value)}>
              {eligibleAccounts.length === 0 && <option value="">No eligible account</option>}
              {eligibleAccounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name}
                  {account.protected ? ' (protected)' : ''}
                </option>
              ))}
            </select>
          </label>
          <label>
            Settlement date
            <input
              type="date"
              value={date}
              onChange={(event) => setDate(event.target.value as LocalDate)}
            />
          </label>
          <label>
            Note (optional)
            <input value={notes} onChange={(event) => setNotes(event.target.value)} />
          </label>
        </div>
        {error && (
          <small className="field-error" role="alert">
            {error}
          </small>
        )}
        <div className="modal-actions">
          <button type="button" className="button secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="button primary" disabled={!accountId}>
            Record settlement
          </button>
        </div>
      </form>
    </Modal>
  );
}

function Insights({ data }: { data: AppData }) {
  const forecast = buildForecast({
    accounts: data.accounts,
    events: plansToEvents(data.plans),
    from: today,
    to: horizonEnd,
    safetyBuffer: data.safetyBuffer,
  });
  const insights = generateStabilityInsights(forecast, data.safetyBuffer);
  const all = insights.length
    ? insights
    : [
        {
          code: 'STABLE',
          severity: 'INFO' as const,
          finding: 'The current scheduled plan remains above your safety buffer.',
          evidence: `The projected low point is ${formatMoney(forecast.minimumAccessibleBalance)} on ${forecast.minimumDate}.`,
          possibleAction:
            'Keep plans current and check again before adding a large discretionary expense.',
        },
      ];
  return (
    <>
      <PageHeading
        eyebrow="Evidence, not guesses"
        title="Financial insights"
        description="Every observation is generated from deterministic rules and your current records."
      />
      <section className="insight-list">
        {all.map((insight) => (
          <article className="panel insight-card" key={insight.code}>
            <div className={`insight-severity ${insight.severity.toLowerCase()}`}>
              <Sparkles />
            </div>
            <div>
              <Status
                text={insight.severity}
                tone={
                  insight.severity === 'CRITICAL'
                    ? 'negative'
                    : insight.severity === 'WARNING'
                      ? 'warning'
                      : 'neutral'
                }
              />
              <h2>{insight.finding}</h2>
              <dl>
                <div>
                  <dt>Evidence</dt>
                  <dd>{insight.evidence}</dd>
                </div>
                <div>
                  <dt>Possible action</dt>
                  <dd>{insight.possibleAction}</dd>
                </div>
              </dl>
            </div>
          </article>
        ))}
      </section>
    </>
  );
}

function Notifications({
  data,
  onMark,
  onMarkAll,
}: {
  data: AppData;
  onMark(id: string): void;
  onMarkAll(): void;
}) {
  return (
    <>
      <PageHeading
        eyebrow="Stay ahead"
        title="Notifications"
        description="Created when the app refreshes your plan; no paid background service required."
        action={
          <button className="button secondary" onClick={onMarkAll}>
            Mark all read
          </button>
        }
      />
      <section className="panel notification-list">
        {data.notifications.map((notification) => (
          <button
            key={notification.id}
            className={`notification-row ${notification.read ? '' : 'unread'}`}
            onClick={() => onMark(notification.id)}
          >
            <span className={`notification-icon ${notification.severity.toLowerCase()}`}>
              <Bell />
            </span>
            <span>
              <strong>{notification.title}</strong>
              <em>{notification.message}</em>
              <small>{new Date(notification.timestamp).toLocaleString()}</small>
            </span>
            {!notification.read && <i aria-label="Unread" />}
          </button>
        ))}
      </section>
    </>
  );
}

function SettingsPage({
  data,
  setData,
  session,
  setToast,
  repository,
}: {
  data: AppData;
  setData(value: AppData): void;
  session: Session | null;
  setToast(value: string): void;
  repository: FinanceRepository | null;
}) {
  const [restorePreview, setRestorePreview] = useState<FinancialBackup | null>(null);
  const [restoreSource, setRestoreSource] = useState<unknown>(null);
  const [editingBuffer, setEditingBuffer] = useState(false);
  const [bufferValue, setBufferValue] = useState((data.safetyBuffer / 100).toFixed(2));
  const [categoryName, setCategoryName] = useState('');
  const [categoryApplicability, setCategoryApplicability] =
    useState<AppCategory['applicability']>('BOTH');
  const [groupName, setGroupName] = useState('');
  function saveSafetyBuffer(event: FormEvent) {
    event.preventDefault();
    const amount = parseMoneyInput(bufferValue);
    if (amount === null) {
      setToast('Enter a valid safety buffer amount, e.g. 25000 or 25000.50.');
      return;
    }
    const save = repository ? repository.updateSafetyBuffer(amount) : Promise.resolve();
    void save
      .then(() => {
        setData({ ...data, safetyBuffer: amount });
        setEditingBuffer(false);
        setToast('Safety buffer updated. New forecasts use it immediately.');
      })
      .catch(() => setToast('The safety buffer could not be saved.'));
  }
  function createCategory(event: FormEvent) {
    event.preventDefault();
    const category: AppCategory = {
      id: crypto.randomUUID(),
      name: categoryName.trim(),
      applicability: categoryApplicability,
      active: true,
    };
    const save = repository ? repository.createCategory(category) : Promise.resolve();
    void save
      .then(() => {
        setData({ ...data, categories: [...data.categories, category] });
        setCategoryName('');
        setToast(`${category.name} category created.`);
      })
      .catch(() => setToast('The category could not be saved.'));
  }
  function createBudgetGroup(event: FormEvent) {
    event.preventDefault();
    const group: AppBudgetGroup = {
      id: crypto.randomUUID(),
      name: groupName.trim(),
      active: true,
    };
    const save = repository ? repository.createBudgetGroup(group) : Promise.resolve();
    void save
      .then(() => {
        setData({ ...data, budgetGroups: [...data.budgetGroups, group] });
        setGroupName('');
        setToast(`${group.name} budget group created.`);
      })
      .catch(() => setToast('The budget group could not be saved.'));
  }
  function archiveCatalog(kind: 'CATEGORY' | 'GROUP', id: string) {
    const save = repository
      ? kind === 'CATEGORY'
        ? repository.archiveCategory(id)
        : repository.archiveBudgetGroup(id)
      : Promise.resolve();
    void save
      .then(() => {
        setData({
          ...data,
          categories: data.categories.map((item) =>
            item.id === id ? { ...item, active: false } : item,
          ),
          budgetGroups: data.budgetGroups.map((item) =>
            item.id === id ? { ...item, active: false } : item,
          ),
        });
        setToast('Item archived. Historical records keep their assignment.');
      })
      .catch(() => setToast('The item could not be archived.'));
  }
  function renameCatalog(kind: 'CATEGORY' | 'GROUP', id: string, currentName: string) {
    const name = window
      .prompt(`Rename ${kind === 'CATEGORY' ? 'category' : 'budget group'}`, currentName)
      ?.trim();
    if (!name || name === currentName) return;
    const save = repository
      ? kind === 'CATEGORY'
        ? repository.renameCategory(id, name)
        : repository.renameBudgetGroup(id, name)
      : Promise.resolve();
    void save
      .then(() => {
        setData({
          ...data,
          categories: data.categories.map((item) => (item.id === id ? { ...item, name } : item)),
          budgetGroups: data.budgetGroups.map((item) =>
            item.id === id ? { ...item, name } : item,
          ),
        });
        setToast('Name updated.');
      })
      .catch(() => setToast('The name could not be updated.'));
  }
  async function exportBackup() {
    const backup = repository
      ? await repository.exportBackup()
      : {
          format: 'expense-assistant-backup',
          version: 1,
          exportedAt: new Date().toISOString(),
          currency: 'LKR',
          records: {
            accounts: data.accounts,
            categories: data.categories,
            budgetGroups: data.budgetGroups,
            recurringRules: [],
            plannedEvents: data.plans,
            transactions: data.transactions,
            settlements: [],
            debts: data.debts,
            debtPayments: [],
            goals: data.goals,
            goalAllocations: [],
            preferences: [{ safetyBuffer: data.safetyBuffer }],
          },
        };
    const url = URL.createObjectURL(
      new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' }),
    );
    const link = document.createElement('a');
    link.href = url;
    link.download = `expense-assistant-backup-${today}.json`;
    link.click();
    URL.revokeObjectURL(url);
    setToast('Backup downloaded. Store it safely off this device.');
  }
  function exportCsv() {
    const escape = (value: string) => {
      const safe = /^[=+\-@]/.test(value) ? `'${value}` : value;
      return `"${safe.replaceAll('"', '""')}"`;
    };
    const rows = [
      ['date', 'type', 'description', 'category', 'group', 'amount_lkr'],
      ...data.transactions.map((item) => [
        item.date,
        item.type,
        item.label,
        item.category,
        item.group,
        (item.amount / 100).toFixed(2),
      ]),
    ];
    const url = URL.createObjectURL(
      new Blob([rows.map((row) => row.map(escape).join(',')).join('\n')], { type: 'text/csv' }),
    );
    const link = document.createElement('a');
    link.href = url;
    link.download = `transactions-${today}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }
  async function previewRestore(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const source: unknown = JSON.parse(await file.text());
      const preview = validateBackup(source);
      if (repository) await repository.previewBackup(source);
      setRestoreSource(source);
      setRestorePreview(preview);
    } catch {
      setToast('Restore stopped: the backup is invalid or unsupported.');
    }
    event.target.value = '';
  }
  function applyRestore() {
    if (!restorePreview || !restoreSource) return;
    if (repository) {
      setToast('Restoring backup atomically…');
      void repository
        .restoreBackup(restoreSource, 'REPLACE', crypto.randomUUID())
        .then(() => {
          setRestorePreview(null);
          setRestoreSource(null);
          markRestoreSuccess();
          window.location.reload();
        })
        .catch(() => setToast('Restore failed. The database transaction was rolled back.'));
      return;
    }
    const records = restorePreview.records;
    setData({
      ...data,
      accounts: records.accounts as unknown as AppData['accounts'],
      transactions: records.transactions as unknown as AppData['transactions'],
      plans: records.plannedEvents as unknown as AppData['plans'],
      goals: records.goals as unknown as AppData['goals'],
      debts: records.debts as unknown as AppData['debts'],
      categories: records.categories as unknown as AppData['categories'],
      budgetGroups: records.budgetGroups as unknown as AppData['budgetGroups'],
    });
    setRestorePreview(null);
    setRestoreSource(null);
    setToast('Backup restored with the replace-current-data strategy.');
  }
  return (
    <>
      <PageHeading
        eyebrow="Workspace"
        title="Settings & data"
        description="Manage your safety buffer, exports, backups, and session."
      />
      <section className="settings-grid">
        <article className="panel settings-card">
          <div className="settings-icon">
            <ShieldCheck />
          </div>
          <h2>Safety rules</h2>
          <p>
            Protected accounts are excluded from spendable cash. The current safety buffer is{' '}
            <strong>{formatMoney(data.safetyBuffer)}</strong>.
          </p>
          {editingBuffer ? (
            <form onSubmit={saveSafetyBuffer} className="inline-edit">
              <label>
                Safety buffer (LKR)
                <input
                  required
                  inputMode="decimal"
                  value={bufferValue}
                  onChange={(event) => setBufferValue(event.target.value)}
                />
              </label>
              <div className="button-row">
                <button
                  type="button"
                  className="button secondary"
                  onClick={() => setEditingBuffer(false)}
                >
                  Cancel
                </button>
                <button className="button primary">Save buffer</button>
              </div>
            </form>
          ) : (
            <button className="button secondary" onClick={() => setEditingBuffer(true)}>
              Edit safety buffer
            </button>
          )}
        </article>
        <article className="panel settings-card catalog-card">
          <div className="settings-icon">
            <WalletCards />
          </div>
          <h2>Categories</h2>
          <p>Create reusable income and expense categories. Archive instead of deleting history.</p>
          <form className="inline-edit" onSubmit={createCategory}>
            <label>
              Category name
              <input
                required
                value={categoryName}
                onChange={(event) => setCategoryName(event.target.value)}
              />
            </label>
            <label>
              Applies to
              <select
                value={categoryApplicability}
                onChange={(event) =>
                  setCategoryApplicability(event.target.value as AppCategory['applicability'])
                }
              >
                <option value="BOTH">Income & expense</option>
                <option value="INCOME">Income</option>
                <option value="EXPENSE">Expense</option>
              </select>
            </label>
            <button className="button secondary">Add category</button>
          </form>
          <div className="catalog-list">
            {data.categories
              .filter((item) => item.active)
              .map((item) => (
                <div key={item.id}>
                  <span>
                    <strong>{item.name}</strong>
                    <small>{titleCase(item.applicability)}</small>
                  </span>
                  <span>
                    <button
                      className="button text"
                      onClick={() => renameCatalog('CATEGORY', item.id, item.name)}
                    >
                      Rename
                    </button>
                    <button
                      className="button text"
                      onClick={() => archiveCatalog('CATEGORY', item.id)}
                    >
                      Archive
                    </button>
                  </span>
                </div>
              ))}
          </div>
        </article>
        <article className="panel settings-card catalog-card">
          <div className="settings-icon">
            <Home />
          </div>
          <h2>Budget groups</h2>
          <p>Organize records by household or another reusable responsibility group.</p>
          <form className="inline-edit" onSubmit={createBudgetGroup}>
            <label>
              Group name
              <input
                required
                value={groupName}
                onChange={(event) => setGroupName(event.target.value)}
              />
            </label>
            <button className="button secondary">Add budget group</button>
          </form>
          <div className="catalog-list">
            {data.budgetGroups
              .filter((item) => item.active)
              .map((item) => (
                <div key={item.id}>
                  <strong>{item.name}</strong>
                  <span>
                    <button
                      className="button text"
                      onClick={() => renameCatalog('GROUP', item.id, item.name)}
                    >
                      Rename
                    </button>
                    <button
                      className="button text"
                      onClick={() => archiveCatalog('GROUP', item.id)}
                    >
                      Archive
                    </button>
                  </span>
                </div>
              ))}
          </div>
        </article>
        <article className="panel settings-card">
          <div className="settings-icon">
            <Download />
          </div>
          <h2>Export your data</h2>
          <p>
            Download a complete versioned backup or a transaction CSV. Authentication secrets are
            never included.
          </p>
          <div className="button-row">
            <button
              className="button secondary"
              onClick={() => void exportBackup().catch(() => setToast('Backup export failed.'))}
            >
              <Download size={17} />
              JSON backup
            </button>
            <button className="button secondary" onClick={exportCsv}>
              CSV
            </button>
          </div>
        </article>
        <article className="panel settings-card">
          <div className="settings-icon">
            <Upload />
          </div>
          <h2>Restore a backup</h2>
          <p>
            V1 validates the file and shows record counts before replacing current records. Export a
            current backup first.
          </p>
          <label className="button secondary file-button">
            <Upload size={17} />
            Choose backup
            <input
              type="file"
              accept="application/json"
              onChange={(event) => void previewRestore(event)}
            />
          </label>
        </article>
        <article className="panel settings-card">
          <div className="settings-icon">
            <CircleDollarSign />
          </div>
          <h2>Session</h2>
          <p>
            {isDemoMode
              ? 'This local demo keeps changes only for the current page session.'
              : `Signed in as ${session?.user.email ?? 'authenticated user'}.`}
          </p>
          {!isDemoMode && (
            <button className="button danger" onClick={() => void supabase?.auth.signOut()}>
              Sign out
            </button>
          )}
        </article>
      </section>
      {restorePreview && (
        <Modal
          title="Restore preview"
          subtitle="Conflict strategy: replace this user’s current financial records."
          onClose={() => {
            setRestorePreview(null);
            setRestoreSource(null);
          }}
        >
          <div className="restore-preview">
            <p>The backup is valid (version {restorePreview.version}) and contains:</p>
            <ul>
              <li>{restorePreview.records.accounts.length} accounts</li>
              <li>{restorePreview.records.transactions.length} transactions</li>
              <li>{restorePreview.records.plannedEvents.length} planned events</li>
              <li>{restorePreview.records.debts.length} debts/receivables</li>
              <li>{restorePreview.records.goals.length} goals</li>
            </ul>
            <p>
              For Supabase workspaces, production restore is applied atomically by the database
              restore operation. Demo restore replaces this page session in one state update.
            </p>
            <div className="modal-actions">
              <button
                className="button secondary"
                onClick={() => {
                  setRestorePreview(null);
                  setRestoreSource(null);
                }}
              >
                Cancel
              </button>
              <button className="button danger" onClick={applyRestore}>
                Replace current data
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

function NewAccount({
  onClose,
  onCreate,
}: {
  onClose(): void;
  onCreate(account: AppAccount): void;
}) {
  const [name, setName] = useState('');
  const [type, setType] = useState('Bank');
  const [balance, setBalance] = useState('0');
  const [minimum, setMinimum] = useState('0');
  const [protectedAccount, setProtectedAccount] = useState(false);
  const [error, setError] = useState('');
  function submit(event: FormEvent) {
    event.preventDefault();
    const openingBalance = parseMoneyInput(balance);
    const minimumBalance = parseMoneyInput(minimum);
    if (openingBalance === null || minimumBalance === null) {
      setError('Enter valid amounts up to 2 decimal places, e.g. 45000 or 45000.50.');
      return;
    }
    setError('');
    onCreate({
      id: crypto.randomUUID(),
      name: name.trim(),
      type,
      balance: openingBalance,
      spendable: true,
      protected: protectedAccount,
      minimumBalance,
    });
  }
  return (
    <Modal
      title="New account"
      subtitle="Balances are derived from this opening amount and actual movements."
      onClose={onClose}
    >
      <form onSubmit={submit}>
        <div className="form-grid">
          <label>
            Account name
            <input required value={name} onChange={(event) => setName(event.target.value)} />
          </label>
          <label>
            Type
            <select value={type} onChange={(event) => setType(event.target.value)}>
              <option>Bank</option>
              <option>Savings</option>
              <option>Cash</option>
              <option>Other</option>
            </select>
          </label>
          <label>
            Opening balance (LKR)
            <input
              required
              inputMode="decimal"
              value={balance}
              onChange={(event) => setBalance(event.target.value)}
            />
          </label>
          <label>
            Minimum balance (LKR)
            <input
              required
              inputMode="decimal"
              value={minimum}
              onChange={(event) => setMinimum(event.target.value)}
            />
          </label>
        </div>
        <label className="check-label">
          <input
            type="checkbox"
            checked={protectedAccount}
            onChange={(event) => setProtectedAccount(event.target.checked)}
          />
          Protect this account from normal spending
        </label>
        {error && (
          <small className="field-error" role="alert">
            {error}
          </small>
        )}
        <div className="modal-actions">
          <button type="button" className="button secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="button primary">Create account</button>
        </div>
      </form>
    </Modal>
  );
}

function NewGoal({ onClose, onCreate }: { onClose(): void; onCreate(goal: AppGoal): void }) {
  const [name, setName] = useState('');
  const [target, setTarget] = useState('');
  const [allocated, setAllocated] = useState('0');
  const [monthly, setMonthly] = useState('0');
  const [targetDate, setTargetDate] = useState(addCalendarDays(today, 365));
  const [error, setError] = useState('');
  function submit(event: FormEvent) {
    event.preventDefault();
    const targetAmount = parseMoneyInput(target);
    const allocatedAmount = parseMoneyInput(allocated);
    const monthlyAmount = parseMoneyInput(monthly);
    if (targetAmount === null || allocatedAmount === null || monthlyAmount === null) {
      setError('Enter valid amounts up to 2 decimal places, e.g. 45000 or 45000.50.');
      return;
    }
    if (allocatedAmount > targetAmount) {
      setError('Already-allocated amount cannot exceed the target amount.');
      return;
    }
    setError('');
    onCreate({
      id: crypto.randomUUID(),
      name: name.trim(),
      target: targetAmount,
      allocated: allocatedAmount,
      targetDate,
      monthlyContribution: monthlyAmount,
      priority: 'MEDIUM',
      status: 'ACTIVE',
    });
  }
  return (
    <Modal
      title="New goal"
      subtitle="No investment return is assumed unless a future scenario explicitly supplies one."
      onClose={onClose}
    >
      <form onSubmit={submit}>
        <div className="form-grid">
          <label>
            Goal name
            <input required value={name} onChange={(event) => setName(event.target.value)} />
          </label>
          <label>
            Target date
            <input
              type="date"
              required
              value={targetDate}
              onChange={(event) => setTargetDate(event.target.value as LocalDate)}
            />
          </label>
          <label>
            Target amount (LKR)
            <input
              required
              inputMode="decimal"
              value={target}
              onChange={(event) => setTarget(event.target.value)}
            />
          </label>
          <label>
            Already allocated (LKR)
            <input
              required
              inputMode="decimal"
              value={allocated}
              onChange={(event) => setAllocated(event.target.value)}
            />
          </label>
          <label>
            Monthly contribution (LKR)
            <input
              required
              inputMode="decimal"
              value={monthly}
              onChange={(event) => setMonthly(event.target.value)}
            />
          </label>
        </div>
        {error && (
          <small className="field-error" role="alert">
            {error}
          </small>
        )}
        <div className="modal-actions">
          <button type="button" className="button secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="button primary">Create goal</button>
        </div>
      </form>
    </Modal>
  );
}

function NewDebt({ onClose, onCreate }: { onClose(): void; onCreate(debt: AppDebt): void }) {
  const [counterparty, setCounterparty] = useState('');
  const [direction, setDirection] = useState<AppDebt['direction']>('I_OWE');
  const [amount, setAmount] = useState('');
  const [dueDate, setDueDate] = useState(addCalendarDays(today, 30));
  const [error, setError] = useState('');
  function submit(event: FormEvent) {
    event.preventDefault();
    const value = parseMoneyInput(amount);
    if (value === null || value <= 0) {
      setError('Enter a valid amount greater than zero, e.g. 45000 or 45000.50.');
      return;
    }
    setError('');
    onCreate({
      id: crypto.randomUUID(),
      counterparty: counterparty.trim(),
      direction,
      originalAmount: value,
      remainingAmount: value,
      dueDate,
      status: 'OPEN',
    });
  }
  return (
    <Modal
      title="New debt or receivable"
      subtitle="Track principal and settle it partially or in full."
      onClose={onClose}
    >
      <form onSubmit={submit}>
        <div className="form-grid">
          <label>
            Counterparty
            <input
              required
              value={counterparty}
              onChange={(event) => setCounterparty(event.target.value)}
            />
          </label>
          <label>
            Direction
            <select
              value={direction}
              onChange={(event) => setDirection(event.target.value as AppDebt['direction'])}
            >
              <option value="I_OWE">I owe them</option>
              <option value="OWED_TO_ME">They owe me</option>
            </select>
          </label>
          <label>
            Original amount (LKR)
            <input
              required
              inputMode="decimal"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
            />
          </label>
          <label>
            Due date
            <input
              type="date"
              value={dueDate}
              onChange={(event) => setDueDate(event.target.value as LocalDate)}
            />
          </label>
        </div>
        {error && (
          <small className="field-error" role="alert">
            {error}
          </small>
        )}
        <div className="modal-actions">
          <button type="button" className="button secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="button primary">Create record</button>
        </div>
      </form>
    </Modal>
  );
}

function QuickAdd({
  data,
  onClose,
  onAdd,
  onPlan,
}: {
  data: AppData;
  onClose(): void;
  onAdd(transaction: AppTransaction): void;
  onPlan(plan: AppPlan, category: string, notes: string, recurring: boolean): Promise<void>;
}) {
  const accounts = data.accounts;
  const categories = data.categories.filter((item) => item.active).map((item) => item.name);
  const budgetGroups = data.budgetGroups.filter((item) => item.active).map((item) => item.name);
  const [type, setType] = useState<AppTransaction['type']>('EXPENSE');
  const [timing, setTiming] = useState<'ACTUAL' | 'PLANNED'>('ACTUAL');
  const [amount, setAmount] = useState('');
  const [label, setLabel] = useState('');
  const [accountId, setAccountId] = useState(
    accounts.find((account) => account.spendable && !account.protected)?.id ??
      accounts[0]?.id ??
      '',
  );
  const [toAccountId, setToAccountId] = useState(
    accounts.find((account) => account.id !== accountId)?.id ?? accountId,
  );
  const [date, setDate] = useState<string>(today);
  const [category, setCategory] = useState('Other');
  const [group, setGroup] = useState('Our Home');
  const [notes, setNotes] = useState('');
  const [recurring, setRecurring] = useState(false);
  const [mandatory, setMandatory] = useState(true);
  const [plannedEventId, setPlannedEventId] = useState('');
  function submit(event: FormEvent) {
    event.preventDefault();
    if (type === 'TRANSFER' && accountId === toAccountId) return;
    const minor = moneyFromMajor(amount);
    const description =
      label.trim() || (type === 'EXPENSE' ? 'Expense' : type === 'INCOME' ? 'Income' : 'Transfer');
    if (timing === 'PLANNED') {
      void onPlan(
        {
          id: crypto.randomUUID(),
          type,
          label: description,
          amount: minor,
          date: date as LocalDate,
          accountId,
          ...(type === 'TRANSFER' ? { toAccountId } : {}),
          group,
          mandatory,
          status: date < today ? 'OVERDUE' : date === today ? 'DUE' : 'UPCOMING',
        },
        type === 'INCOME' && category === 'Other' ? 'Salary' : category,
        notes.trim(),
        recurring,
      );
      return;
    }
    onAdd({
      id: crypto.randomUUID(),
      type,
      label: description,
      amount: minor,
      date: date as LocalDate,
      accountId,
      ...(type === 'TRANSFER' ? { toAccountId } : {}),
      category: type === 'INCOME' && category === 'Other' ? 'Salary' : category,
      group,
      ...(notes.trim() ? { notes: notes.trim() } : {}),
      ...(plannedEventId ? { plannedEventId } : {}),
      status: 'COMPLETED',
    });
  }
  return (
    <Modal
      title="Quick add"
      subtitle={
        timing === 'ACTUAL'
          ? 'Record an actual money movement'
          : 'Add an expected item without moving money yet'
      }
      onClose={onClose}
    >
      <form onSubmit={submit}>
        <div className="segmented timing-segmented">
          <button
            type="button"
            className={timing === 'ACTUAL' ? 'active' : ''}
            onClick={() => setTiming('ACTUAL')}
          >
            Completed now
          </button>
          <button
            type="button"
            className={timing === 'PLANNED' ? 'active' : ''}
            onClick={() => setTiming('PLANNED')}
          >
            Planned
          </button>
        </div>
        <div className="segmented">
          {(['EXPENSE', 'INCOME', 'TRANSFER'] as const).map((item) => (
            <button
              type="button"
              className={type === item ? 'active' : ''}
              key={item}
              onClick={() => setType(item)}
            >
              {item === 'EXPENSE' ? (
                <ArrowUpRight />
              ) : item === 'INCOME' ? (
                <ArrowDownLeft />
              ) : (
                <ArrowLeftRight />
              )}
              {titleCase(item)}
            </button>
          ))}
        </div>
        <label className="amount-input">
          <span>Amount (LKR)</span>
          <div>
            <small>Rs.</small>
            <input
              autoFocus
              inputMode="decimal"
              placeholder="0"
              required
              pattern="\d+(\.\d{1,2})?"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
            />
          </div>
        </label>
        <div className="form-grid">
          <label>
            Description
            <input
              placeholder="What was this for?"
              value={label}
              onChange={(event) => setLabel(event.target.value)}
            />
          </label>
          <label>
            Date
            <input
              type="date"
              required
              value={date}
              onChange={(event) => setDate(event.target.value)}
            />
          </label>
          <label>
            {type === 'TRANSFER' ? 'From account' : 'Account'}
            <select value={accountId} onChange={(event) => setAccountId(event.target.value)}>
              {accounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name}
                </option>
              ))}
            </select>
          </label>
          {type === 'TRANSFER' ? (
            <>
              <label>
                To account
                <select
                  value={toAccountId}
                  onChange={(event) => setToAccountId(event.target.value)}
                >
                  {accounts
                    .filter((account) => account.id !== accountId)
                    .map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.name}
                      </option>
                    ))}
                </select>
              </label>
              <label>
                Notes
                <input
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  placeholder="Optional details"
                />
              </label>
            </>
          ) : (
            <>
              <label>
                Category
                <select value={category} onChange={(event) => setCategory(event.target.value)}>
                  {categories.map((item) => (
                    <option key={item}>{item}</option>
                  ))}
                </select>
              </label>
              <label>
                Budget group
                <select value={group} onChange={(event) => setGroup(event.target.value)}>
                  {budgetGroups.map((item) => (
                    <option key={item}>{item}</option>
                  ))}
                </select>
              </label>
              <label>
                Notes
                <input
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  placeholder="Optional details"
                />
              </label>
            </>
          )}
          {timing === 'ACTUAL' && (
            <label>
              Settle planned item
              <select
                value={plannedEventId}
                onChange={(event) => setPlannedEventId(event.target.value)}
              >
                <option value="">None</option>
                {data.plans
                  .filter((plan) => plan.type === type && plan.status !== 'SETTLED')
                  .map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {plan.label} · {formatMoney(plan.amount)}
                    </option>
                  ))}
              </select>
            </label>
          )}
        </div>
        {type === 'TRANSFER' && accountId === toAccountId && (
          <p className="form-message">Choose two different owned accounts.</p>
        )}
        {timing === 'PLANNED' && (
          <div className="planning-options">
            <label className="check-label">
              <input
                type="checkbox"
                checked={recurring}
                onChange={(event) => setRecurring(event.target.checked)}
              />
              Repeat monthly on this day
            </label>
            <label className="check-label">
              <input
                type="checkbox"
                checked={mandatory}
                onChange={(event) => setMandatory(event.target.checked)}
              />
              Mandatory commitment
            </label>
          </div>
        )}
        <div className="modal-actions">
          <button type="button" className="button secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="button primary"
            disabled={type === 'TRANSFER' && accountId === toAccountId}
          >
            {timing === 'ACTUAL'
              ? `Record ${titleCase(type)}`
              : `Create ${recurring ? 'recurring ' : ''}plan`}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function Affordability({
  data,
  onClose,
  onProceed,
  onSchedule,
}: {
  data: AppData;
  onClose(): void;
  onProceed(transaction: AppTransaction): void;
  onSchedule(plan: AppPlan, category: string, notes: string): Promise<void>;
}) {
  const [amount, setAmount] = useState('45000');
  const [amountError, setAmountError] = useState('');
  const [date, setDate] = useState<string>(today);
  const [accountId, setAccountId] = useState(
    data.accounts.find((account) => account.spendable && !account.protected)?.id ?? '',
  );
  const [importance, setImportance] = useState<'ESSENTIAL' | 'IMPORTANT' | 'DISCRETIONARY'>(
    'DISCRETIONARY',
  );
  const [description, setDescription] = useState('Proposed purchase');
  const [category, setCategory] = useState('Shopping');
  const [group, setGroup] = useState('Our Home');
  const [horizonDays, setHorizonDays] = useState(60);
  const [result, setResult] = useState<AffordabilityResult | null>(null);
  // The amount actually used for `result`, captured at assessment time so
  // render never re-parses the (possibly now-invalid) live `amount` field.
  const [assessedAmount, setAssessedAmount] = useState<number | null>(null);
  const navigate = useNavigate();
  function clearAssessment() {
    setResult(null);
    setAssessedAmount(null);
  }
  function assess(event: FormEvent) {
    event.preventDefault();
    const parsedAmount = parseMoneyInput(amount);
    if (parsedAmount === null || parsedAmount <= 0) {
      setAmountError('Enter a valid amount up to 2 decimal places, e.g. 45000 or 45000.50.');
      clearAssessment();
      return;
    }
    setAmountError('');
    setAssessedAmount(parsedAmount);
    setResult(
      assessAffordability({
        accounts: data.accounts,
        events: plansToEvents(data.plans),
        from: date as LocalDate,
        to: addCalendarDays(date as LocalDate, horizonDays),
        safetyBuffer: data.safetyBuffer,
        committedGoalContribution: data.goals
          .filter((goal) => goal.status === 'ACTIVE')
          .reduce((sum, goal) => sum + goal.monthlyContribution, 0),
        proposal: {
          amount: parsedAmount,
          date: date as LocalDate,
          accountId,
          description: description.trim() || 'Proposed purchase',
          importance,
        },
      }),
    );
  }
  function proceed() {
    if (assessedAmount === null) return;
    onProceed({
      id: crypto.randomUUID(),
      type: 'EXPENSE',
      label: description.trim() || 'Proposed purchase',
      amount: assessedAmount,
      date: date as LocalDate,
      accountId,
      category,
      group,
      status: 'COMPLETED',
    });
  }
  function schedule() {
    if (assessedAmount === null) return Promise.resolve();
    return onSchedule(
      {
        id: crypto.randomUUID(),
        type: 'EXPENSE',
        label: description.trim() || 'Proposed purchase',
        amount: assessedAmount,
        date: date as LocalDate,
        accountId,
        group,
        mandatory: importance === 'ESSENTIAL',
        status: date < today ? 'OVERDUE' : date === today ? 'DUE' : 'UPCOMING',
      },
      category,
      'Scheduled from affordability assessment',
    );
  }
  return (
    <Modal
      wide
      title="Can I afford this?"
      subtitle="A simulation only — nothing is recorded until you choose to proceed."
      onClose={onClose}
    >
      <form onSubmit={assess}>
        <div className="afford-form">
          <label className="amount-input">
            <span>Proposed amount (LKR)</span>
            <div>
              <small>Rs.</small>
              <input
                autoFocus
                inputMode="decimal"
                required
                aria-invalid={amountError ? true : undefined}
                value={amount}
                onChange={(event) => {
                  setAmount(event.target.value);
                  setAmountError('');
                  clearAssessment();
                }}
              />
            </div>
            {amountError && (
              <small className="field-error" role="alert">
                {amountError}
              </small>
            )}
          </label>
          <div className="form-grid">
            <label>
              Purchase date
              <input
                type="date"
                value={date}
                onChange={(event) => {
                  setDate(event.target.value);
                  clearAssessment();
                }}
              />
            </label>
            <label>
              Paying account
              <select
                value={accountId}
                onChange={(event) => {
                  setAccountId(event.target.value);
                  clearAssessment();
                }}
              >
                {data.accounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name}
                    {account.protected ? ' (protected)' : ''}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Importance
              <select
                value={importance}
                onChange={(event) => {
                  setImportance(event.target.value as typeof importance);
                  clearAssessment();
                }}
              >
                <option value="ESSENTIAL">Essential</option>
                <option value="IMPORTANT">Important</option>
                <option value="DISCRETIONARY">Discretionary</option>
              </select>
            </label>
            <label>
              Description
              <input value={description} onChange={(event) => setDescription(event.target.value)} />
            </label>
            <label>
              Category
              <input value={category} onChange={(event) => setCategory(event.target.value)} />
            </label>
            <label>
              Budget group
              <input value={group} onChange={(event) => setGroup(event.target.value)} />
            </label>
            <label>
              Forecast horizon
              <select
                value={horizonDays}
                onChange={(event) => {
                  setHorizonDays(Number(event.target.value));
                  clearAssessment();
                }}
              >
                <option value={60}>60 days</option>
                <option value={90}>90 days</option>
                <option value={183}>6 months</option>
                <option value={365}>12 months</option>
              </select>
            </label>
          </div>
          <button className="button primary full">Run cash-flow check</button>
        </div>
      </form>
      {result && assessedAmount !== null && (
        <div className={`assessment-result ${result.classification.toLowerCase()}`}>
          <div className="assessment-head">
            <div>
              <p className="card-label">ASSESSMENT</p>
              <h2>{titleCase(result.classification)}</h2>
            </div>
            <Gauge size={32} />
          </div>
          <p className="assessment-lead">
            {result.classification === 'SAFE'
              ? 'This expense fits the current plan while preserving configured safeguards.'
              : result.classification === 'MANAGEABLE_WITH_TRADEOFF'
                ? 'Mandatory obligations remain covered, but another plan loses capacity.'
                : result.classification === 'HIGH_RISK'
                  ? 'You can pay now, but the plan crosses a configured safeguard.'
                  : 'The timeline contains a cash or mandatory-obligation shortfall.'}
          </p>
          <ul>
            {result.evidence.map((evidence) => (
              <li key={evidence}>{evidence}</li>
            ))}
          </ul>
          <div className="assessment-stats">
            <span>
              After purchase<strong>{formatMoney(result.immediateBalance)}</strong>
            </span>
            <span>
              Lowest point<strong>{formatMoney(result.forecast.minimumAccessibleBalance)}</strong>
            </span>
            <span>
              Lower safe amount<strong>{formatMoney(result.lowerSafeAmount)}</strong>
            </span>
          </div>
          {(result.lowerSafeAmount < assessedAmount ||
            result.saferDates.length > 0 ||
            result.alternativeAccounts.length > 0) && (
            <div className="scenario-comparisons">
              <p className="card-label">COMPARE SCENARIOS</p>
              {result.lowerSafeAmount < assessedAmount && (
                <button
                  type="button"
                  className="scenario-option"
                  onClick={() => {
                    setAmount((result.lowerSafeAmount / 100).toFixed(2));
                    setAmountError('');
                    clearAssessment();
                  }}
                >
                  <span>Lower amount · {formatMoney(result.lowerSafeAmount)}</span>
                  <Status text="SAFE TARGET" tone="positive" />
                </button>
              )}
              {[...result.saferDates, ...result.alternativeAccounts].map((alternative) => (
                <button
                  type="button"
                  className="scenario-option"
                  key={`${alternative.kind}-${alternative.date}-${alternative.accountId}`}
                  onClick={() => {
                    setAmount((alternative.amount / 100).toFixed(2));
                    setDate(alternative.date);
                    setAccountId(alternative.accountId);
                    setAmountError('');
                    clearAssessment();
                  }}
                >
                  <span>
                    {alternative.kind === 'SAFER_DATE'
                      ? alternative.date
                      : data.accounts.find((item) => item.id === alternative.accountId)?.name}
                  </span>
                  <Status
                    text={alternative.classification}
                    tone={
                      alternative.classification === 'SAFE'
                        ? 'positive'
                        : alternative.classification === 'MANAGEABLE_WITH_TRADEOFF'
                          ? 'warning'
                          : 'negative'
                    }
                  />
                </button>
              ))}
            </div>
          )}
          <details>
            <summary>Assumptions used</summary>
            <ul>
              {result.assumptions.map((assumption) => (
                <li key={assumption}>{assumption}</li>
              ))}
            </ul>
          </details>
          <div className="modal-actions">
            <button type="button" className="button secondary" onClick={clearAssessment}>
              Change scenario
            </button>
            <button
              type="button"
              className="button secondary"
              onClick={() => void schedule().then(() => navigate('/planner'))}
            >
              Schedule for later
            </button>
            <button type="button" className="button primary" onClick={proceed}>
              Proceed & record
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function PageHeading({
  eyebrow,
  title,
  description,
  action,
}: {
  eyebrow: string;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <header className="page-heading">
      <div>
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        {description && <p>{description}</p>}
      </div>
      {action && <div>{action}</div>}
    </header>
  );
}
function Metric({
  label,
  value,
  icon,
  tone,
  detail,
}: {
  label: string;
  value: string;
  icon: ReactNode;
  tone: string;
  detail?: string;
}) {
  return (
    <article className="metric-card">
      <span className={`metric-icon ${tone}`}>{icon}</span>
      <div>
        <p>{label}</p>
        <strong>{value}</strong>
        {detail && <small>{detail}</small>}
      </div>
    </article>
  );
}
function PanelTitle({ title, meta, link }: { title: string; meta?: string; link?: string }) {
  return (
    <header className="panel-title">
      <div>
        <h2>{title}</h2>
        {meta && <p>{meta}</p>}
      </div>
      {link && (
        <NavLink to={link}>
          View all <ChevronRight size={16} />
        </NavLink>
      )}
    </header>
  );
}
function PlanRow({
  plan,
  onSettle,
}: {
  plan: AppPlan;
  onSettle(plan: AppPlan, amount?: number): void;
}) {
  return (
    <div className="plan-row">
      <span className={`transaction-icon ${plan.type.toLowerCase()}`}>
        {plan.type === 'INCOME' ? (
          <ArrowDownLeft />
        ) : plan.type === 'TRANSFER' ? (
          <ArrowLeftRight />
        ) : (
          <ArrowUpRight />
        )}
      </span>
      <span className="plan-main">
        <strong>{plan.label}</strong>
        <small>
          {plan.group} · {plan.date}
        </small>
      </span>
      <span className={`plan-amount ${plan.type.toLowerCase()}`}>
        {plan.type === 'INCOME' ? '+' : plan.type === 'EXPENSE' ? '−' : ''}
        {formatMoney(plan.amount)}
      </span>
      <Status
        text={plan.status}
        tone={
          plan.status === 'SETTLED'
            ? 'positive'
            : plan.status === 'OVERDUE'
              ? 'negative'
              : 'neutral'
        }
      />
      {plan.status !== 'SETTLED' && (
        <span className="row-buttons">
          <button className="row-action" onClick={() => onSettle(plan)}>
            {plan.type === 'INCOME' ? 'Received' : plan.type === 'TRANSFER' ? 'Moved' : 'Paid'}
          </button>
          <button
            className="row-action"
            onClick={() => {
              const value = window.prompt('Partial settlement amount (LKR)');
              if (!value) return;
              try {
                onSettle(plan, moneyFromMajor(value));
              } catch {
                window.alert('Enter a valid amount up to the remaining balance.');
              }
            }}
          >
            Partial
          </button>
        </span>
      )}
    </div>
  );
}
function ActualRow({ transaction }: { transaction: AppTransaction }) {
  return (
    <div className="plan-row actual-row">
      <span className={`transaction-icon ${transaction.type.toLowerCase()}`}>
        {transaction.type === 'INCOME' ? (
          <ArrowDownLeft />
        ) : transaction.type === 'TRANSFER' ? (
          <ArrowLeftRight />
        ) : (
          <ArrowUpRight />
        )}
      </span>
      <span className="plan-main">
        <strong>{transaction.label}</strong>
        <small>{transaction.group} · Actual</small>
      </span>
      <span className={`plan-amount ${transaction.type.toLowerCase()}`}>
        {transaction.type === 'INCOME' ? '+' : transaction.type === 'EXPENSE' ? '−' : ''}
        {formatMoney(transaction.amount)}
      </span>
      <Status text="RECORDED" tone="positive" />
    </div>
  );
}
function Status({
  text,
  tone,
}: {
  text: string;
  tone: 'positive' | 'negative' | 'warning' | 'neutral';
}) {
  return <span className={`status ${tone}`}>{text.replaceAll('_', ' ')}</span>;
}
function SummaryLine({
  label,
  value,
  tone,
  large,
}: {
  label: string;
  value: string;
  tone: string;
  large?: boolean;
}) {
  return (
    <div className={`summary-line ${large ? 'large' : ''}`}>
      <span>{label}</span>
      <strong className={`${tone}-text`}>{value}</strong>
    </div>
  );
}
function Empty({ title, body }: { title: string; body: string }) {
  return (
    <div className="empty">
      <Search />
      <strong>{title}</strong>
      <p>{body}</p>
    </div>
  );
}
function Modal({
  title,
  subtitle,
  onClose,
  wide,
  children,
}: {
  title: string;
  subtitle: string;
  onClose(): void;
  wide?: boolean;
  children: ReactNode;
}) {
  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className={`modal ${wide ? 'wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        <header>
          <div>
            <h2 id="modal-title">{title}</h2>
            <p>{subtitle}</p>
          </div>
          <button className="icon-button" aria-label="Close" onClick={onClose}>
            <X />
          </button>
        </header>
        {children}
      </section>
    </div>
  );
}

function plansToEvents(plans: AppPlan[]): CashFlowEvent[] {
  return plans
    .filter((plan) => plan.status !== 'SETTLED')
    .map((plan) => ({
      id: plan.id,
      date: plan.date,
      label: plan.label,
      amount: plan.type === 'EXPENSE' ? -plan.amount : plan.amount,
      ...(plan.type === 'TRANSFER'
        ? {
            sourceAccountId: plan.accountId,
            ...(plan.toAccountId ? { destinationAccountId: plan.toAccountId } : {}),
          }
        : { accountId: plan.accountId }),
      kind: plan.type,
      mandatory: plan.mandatory,
    }));
}
function referenceMetrics(plans: AppPlan[]) {
  return calculateMonthlyMetrics(
    plans.map((plan) => ({ type: plan.type, amount: plan.originalAmount ?? plan.amount })),
  );
}
function notificationCandidates(data: AppData) {
  const forecast = buildForecast({
    accounts: data.accounts,
    events: plansToEvents(data.plans),
    from: today,
    to: horizonEnd,
    safetyBuffer: data.safetyBuffer,
  });
  const monthlyCapacity = referenceMetrics(
    data.plans.filter((plan) => plan.date.startsWith(today.slice(0, 7))),
  ).surplus;
  return generateNotifications({
    asOfDate: today,
    forecast,
    plannedEvents: data.plans
      .filter((plan): plan is AppPlan & { type: 'INCOME' | 'EXPENSE' } => plan.type !== 'TRANSFER')
      .map((plan) => ({
        id: plan.id,
        label: plan.label,
        type: plan.type,
        status: plan.status,
        dueWindowStart: plan.date,
        dueWindowEnd: plan.date,
        remainingAmount: plan.status === 'SETTLED' ? 0 : plan.amount,
      })),
    debts: data.debts.map((debt) => ({
      id: debt.id,
      counterparty: debt.counterparty,
      direction: debt.direction === 'I_OWE' ? ('PAYABLE' as const) : ('RECEIVABLE' as const),
      status:
        debt.status === 'SETTLED'
          ? ('SETTLED' as const)
          : debt.status === 'OVERDUE'
            ? ('OVERDUE' as const)
            : ('ACTIVE' as const),
      remainingAmount: debt.remainingAmount,
      dueDate: debt.dueDate,
    })),
    goals: data.goals.map((goal) => {
      const result = evaluateGoal({
        targetAmount: goal.target,
        currentAllocated: goal.allocated,
        targetDate: goal.targetDate,
        asOfDate: today,
        plannedMonthlyContribution: goal.monthlyContribution,
        forecastMonthlyFreeCash: monthlyCapacity,
      });
      return {
        id: goal.id,
        name: goal.name,
        contributionGap: result.contributionGap,
        feasible: result.feasibleAtPlannedContribution && result.cashFlowFeasible,
        targetDate: goal.targetDate,
      };
    }),
    backupReminderDays: 30,
  });
}
function titleCase(value: string) {
  return value
    .toLowerCase()
    .replaceAll('_', ' ')
    .replace(/(^|\s)\S/g, (letter) => letter.toUpperCase());
}
function financialToday(timeZone: string): LocalDate {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const get = (kind: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === kind)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}` as LocalDate;
}
function addCalendarDays(value: LocalDate, days: number): LocalDate {
  const date = new Date(`${value}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10) as LocalDate;
}
function formatFinancialDate(value: LocalDate): string {
  return new Intl.DateTimeFormat('en-LK', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(new Date(`${value}T00:00:00Z`));
}
function isAffordabilityToolInput(value: unknown): value is {
  amountLkr: number;
  date: string;
  accountId: string;
  importance: 'ESSENTIAL' | 'IMPORTANT' | 'DISCRETIONARY';
} {
  if (!value || typeof value !== 'object') return false;
  const input = value as Record<string, unknown>;
  return (
    typeof input.amountLkr === 'number' &&
    Number.isFinite(input.amountLkr) &&
    input.amountLkr > 0 &&
    typeof input.date === 'string' &&
    /^\d{4}-\d{2}-\d{2}$/.test(input.date) &&
    typeof input.accountId === 'string' &&
    (input.importance === 'ESSENTIAL' ||
      input.importance === 'IMPORTANT' ||
      input.importance === 'DISCRETIONARY')
  );
}

export default App;
